import { ChangeDetectionStrategy, Component, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { IdleTimeoutService } from '../../core/auth/idle-timeout.service';
import { CuponsRecentesService } from '../../core/cupons-recentes/cupons-recentes.service';
import { LogoComponent } from '../logo/logo.component';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, LogoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="shell">
      <aside class="shell__side">
        <div class="shell__brand">
          <cs-logo [size]="22" [showWordmark]="true"></cs-logo>
        </div>

        <nav class="shell__nav">
          @if (isAdmin()) {
            <a routerLink="/inicio" routerLinkActive="shell__nav-item--active" class="shell__nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z"/>
              </svg>
              <span>Visão geral</span>
            </a>
          }
          @if (isAdmin()) {
            <a routerLink="/cupons" routerLinkActive="shell__nav-item--active" class="shell__nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                <line x1="8" y1="6" x2="21" y2="6"/>
                <line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <circle cx="4" cy="6" r="1.2"/>
                <circle cx="4" cy="12" r="1.2"/>
                <circle cx="4" cy="18" r="1.2"/>
              </svg>
              <span>Cupons</span>
            </a>
          }
          <a routerLink="/conferencia" routerLinkActive="shell__nav-item--active" class="shell__nav-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
              <circle cx="12" cy="12" r="9"/>
              <polyline points="8 12 11 15 16 9"/>
            </svg>
            <span>Conferência</span>
          </a>
          @if (isAdmin()) {
            <a routerLink="/relatorios" routerLinkActive="shell__nav-item--active" class="shell__nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                <path d="M3 3v18h18"/>
                <rect x="7" y="11" width="3" height="6"/>
                <rect x="13" y="7" width="3" height="10"/>
              </svg>
              <span>Relatórios</span>
            </a>
          }
          @if (isAdmin()) {
            <a routerLink="/gatilhos" routerLinkActive="shell__nav-item--active" class="shell__nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span>Gatilhos</span>
            </a>
          }
          @if (isAdmin()) {
            <a routerLink="/usuarios" routerLinkActive="shell__nav-item--active" class="shell__nav-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span>Usuários</span>
            </a>
          }
        </nav>

        @if (cuponsRecentes().length > 0) {
          <div class="shell__recents">
            <div class="shell__recents-title">Cupons recentes</div>
            @for (c of cuponsRecentes(); track c.codigo) {
              <a class="shell__recents-item"
                 [class.shell__recents-item--active]="c.ativo"
                 [routerLink]="['/conferencia']"
                 [queryParams]="{ modo: 'cupom', q: c.codigo }">
                <span class="shell__recents-code">{{ c.codigo }}</span>
                @if (c.ativo) {
                  <span class="shell__recents-badge">VENDO</span>
                }
              </a>
            }
          </div>
        }

        <div class="shell__user">
          <button type="button" class="shell__sair" (click)="sair()">Sair</button>
        </div>
      </aside>

      <main class="shell__main">
        <router-outlet></router-outlet>
      </main>
    </div>
  `,
  styles: [`
    :host { display: block; }

    .shell {
      display: grid;
      grid-template-columns: 240px 1fr;
      min-height: 100vh;
      background: rgb(var(--color-bg));
      color: rgb(var(--color-ink));
      font-family: var(--font-sans);
    }

    .shell__side {
      border-right: 1px solid rgb(var(--color-line));
      background: rgb(var(--color-bg-soft));
      padding: 24px 16px;
      display: flex;
      flex-direction: column;
      gap: 24px;
      position: sticky;
      top: 0;
      height: 100vh;
      overflow-y: auto;
    }

    .shell__brand {
      padding: 0 6px 16px;
      border-bottom: 1px solid rgb(var(--color-line));
    }

    .shell__nav { display: flex; flex-direction: column; gap: 2px; }
    .shell__nav-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border-radius: var(--radius-sm);
      font-size: 13px;
      font-weight: 500;
      color: rgb(var(--color-ink-2));
      text-decoration: none;
      cursor: pointer;
      transition: background-color 120ms, color 120ms;

      svg { width: 16px; height: 16px; flex-shrink: 0; opacity: 0.85; }
    }
    .shell__nav-item:hover { background: rgb(var(--color-hover)); }
    .shell__nav-item--active {
      background: rgb(var(--color-accent-bg));
      color: rgb(var(--color-accent-2));
      font-weight: 600;

      svg { opacity: 1; }
    }

    .shell__recents {
      padding: 16px 10px 0;
      border-top: 1px solid rgb(var(--color-line));
    }
    .shell__recents-title {
      font-size: 11px;
      font-weight: 600;
      color: rgb(var(--color-ink-3));
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 10px;
    }
    .shell__recents-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 0;
      font-size: 12px;
      font-family: var(--font-mono);
      color: rgb(var(--color-ink-3));
      cursor: pointer;
      text-decoration: none;
    }
    .shell__recents-item--active {
      color: rgb(var(--color-ink));
      font-weight: 600;
    }
    .shell__recents-badge {
      font-size: 9px;
      padding: 2px 5px;
      background: rgb(var(--color-accent));
      color: rgb(var(--color-paper));
      border-radius: 3px;
      letter-spacing: 0.05em;
      font-family: var(--font-sans);
      font-weight: 700;
    }

    .shell__user {
      margin-top: auto;
      padding: 16px 10px 0;
      border-top: 1px solid rgb(var(--color-line));
    }
    .shell__sair {
      width: 100%;
      padding: 8px 10px;
      border: 1px solid rgb(var(--color-line-2));
      border-radius: var(--radius-sm);
      background: rgb(var(--color-paper));
      color: rgb(var(--color-ink-2));
      cursor: pointer;
      font-size: 12px;
      font-family: inherit;
    }
    .shell__sair:hover {
      background: rgb(var(--color-hover));
      color: rgb(var(--color-ink));
    }

    .shell__main { min-width: 0; }

    @media (max-width: 900px) {
      .shell { grid-template-columns: 1fr; }
      .shell__side {
        position: static; height: auto;
        flex-direction: row; align-items: center;
        gap: 16px; padding: 12px 16px; flex-wrap: wrap;
      }
      .shell__brand { padding: 0; border: none; }
      .shell__nav { flex-direction: row; flex-wrap: wrap; }
      .shell__recents { display: none; }
      .shell__user { margin: 0 0 0 auto; padding: 0; border: none; }
      .shell__sair { width: auto; }
    }
  `],
})
export class AppShellComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly idle = inject(IdleTimeoutService);
  private readonly recentesSvc = inject(CuponsRecentesService);

  protected readonly isAdmin = this.auth.isAdmin;

  private readonly cupomAtivo = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const url = this.router.url;
        if (!url.startsWith('/conferencia')) return null;
        const qIdx = url.indexOf('?');
        if (qIdx === -1) return null;
        const params = new URLSearchParams(url.substring(qIdx + 1));
        if (params.get('modo') !== 'cupom') return null;
        return params.get('q');
      }),
    ),
    { initialValue: null },
  );

  protected readonly cuponsRecentes = computed(() => {
    const ativo = this.cupomAtivo();
    return this.recentesSvc.lista().map(c => ({
      codigo: c.codigo,
      ativo: ativo != null && c.codigo === ativo,
    }));
  });

  ngOnInit(): void {
    this.idle.start();
  }

  ngOnDestroy(): void {
    this.idle.stop();
  }

  sair(): void {
    this.idle.stop();
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }
}
